import {
  GameController,
  type DifficultyKey,
  DIFFICULTY_PRESET_ORDER,
  type GameSnapshot,
} from './game-controller';
import {
  loadSettings,
  saveSettings,
  type StoredSettings,
} from './storage';
import type {
  BeforeInstallPromptEvent,
  InstallOutcome,
  InstallState,
  PwaStatusSnapshot,
  ServiceWorkerState,
} from './pwa/types';

export interface LetterRainAppControls {
  destroy: () => void;
  updateStatus: (message: string) => void;
  debug?: {
    step: (milliseconds: number, options?: { force?: boolean }) => void;
    isRunning: () => boolean;
    letters: () => Array<{ id: string; y: number; alive: boolean }>;
  };
  pwa: {
    setInstallPrompt: (event: BeforeInstallPromptEvent | null) => void;
    markInstalled: () => void;
    reportInstallOutcome: (outcome: InstallOutcome) => void;
    setServiceWorkerState: (state: ServiceWorkerState) => void;
    getStatus: () => PwaStatusSnapshot;
  };
}

interface AppInitOptions {
  seed?: number | string;
}

const DIFFICULTY_PRESETS = DIFFICULTY_PRESET_ORDER;
const DIFFICULTY_INDEX_BY_KEY = new Map<DifficultyKey, number>(
  DIFFICULTY_PRESETS.map((preset, index) => [preset.key, index])
);

const DIFFICULTY_ALIAS: Record<string, DifficultyKey> = {
  zen: 'level1',
  gentle: 'level2',
  easy: 'level2',
  level1: 'level1',
  level2: 'level2',
  normal: 'level3',
  standard: 'level3',
  level3: 'level3',
  pulse: 'level4',
  brisk: 'level4',
  level4: 'level4',
  surge: 'level6',
  storm: 'level6',
  level5: 'level5',
  level6: 'level6',
};

function normalizeDifficultyKey(value: string | undefined | null): DifficultyKey | undefined {
  if (!value) {
    return undefined;
  }
  if (DIFFICULTY_INDEX_BY_KEY.has(value as DifficultyKey)) {
    return value as DifficultyKey;
  }
  return DIFFICULTY_ALIAS[value] ?? undefined;
}

const THEME_OPTIONS = [
  { value: 'midnight', label: 'Blue' },
  { value: 'aurora', label: 'Seafoam' },
  { value: 'ember', label: 'Amber' },
] as const;

type ThemeKey = (typeof THEME_OPTIONS)[number]['value'];

const SEED_PATTERN = /^[a-z0-9-]{6,32}$/i;

function generateSeed(): string {
  if (typeof globalThis.crypto?.getRandomValues === 'function') {
    const buffer = new Uint32Array(2);
    globalThis.crypto.getRandomValues(buffer);
    return `${buffer[0].toString(36)}${buffer[1].toString(36)}`.slice(0, 16);
  }
  const raw = Math.random().toString(36).slice(2, 14);
  return raw.padEnd(8, '0').slice(0, 12);
}

function normalizeSeed(value: number | string | undefined): string {
  if (value === undefined || value === null) {
    return generateSeed();
  }
  const seed = typeof value === 'number' ? Math.abs(value).toString(36) : String(value);
  const cleaned = seed.trim().toLowerCase();
  if (!SEED_PATTERN.test(cleaned)) {
    return generateSeed();
  }
  return cleaned.slice(0, 32);
}

function createLiveRegion(doc: Document): HTMLDivElement {
  const region = doc.createElement('div');
  region.className = 'sr-only';
  region.setAttribute('role', 'status');
  region.setAttribute('aria-live', 'polite');
  region.setAttribute('aria-atomic', 'true');
  region.textContent = 'Current streak 0. Best streak 0.';
  return region;
}

function applyTheme(shell: HTMLElement, theme: ThemeKey): void {
  shell.dataset.theme = theme;
}

export function initApp(root: HTMLElement, options: AppInitOptions = {}): LetterRainAppControls {
  const doc = root.ownerDocument ?? document;
  root.setAttribute('role', 'application');
  root.setAttribute('aria-label', 'Letter Rain play area');

  const themeOptionsMarkup = THEME_OPTIONS.map(
    (option) => `<option value="${option.value}">${option.label}</option>`
  ).join('');

  const shell = doc.createElement('main');
  shell.className = 'app-shell';
  shell.style.setProperty('--font-scale', '1');
  shell.style.setProperty('--keyboard-height', '160px');
  shell.innerHTML = `
    <header class="streak-bar" aria-label="Streak summary">
      <span class="streak-value" data-streak data-streak-indicator aria-label="Current streak">0</span>
      <span class="streak-value streak-value--best" data-best-streak data-best-indicator aria-label="Best streak">0</span>
    </header>
    <section class="stage" aria-label="fall zone">
      <div class="playfield rain-stage--empty" data-stage></div>
      <div class="badge-stack" data-badge-stack></div>
    </section>
    <section class="virtual-keyboard-host" data-keyboard></section>
    <footer class="controls" aria-label="game controls">
      <div class="controls-group controls-group--difficulty" role="group" aria-label="difficulty">
        <label for="difficulty">Difficulty</label>
        <input id="difficulty" type="range" min="0" step="1" value="0" />
      </div>
      <div class="controls-group" role="group" aria-label="theme">
        <label for="theme">Theme</label>
        <select id="theme">${themeOptionsMarkup}</select>
      </div>
      <div class="controls-group controls-group--toggle" role="group" aria-label="tap targets">
        <label for="tap-mode">
          <input type="checkbox" id="tap-mode" checked />
          <span>Tap targets</span>
        </label>
        <p class="controls-note controls-note--tap">Tap falling letters to clear them.</p>
      </div>
      <div
        class="controls-group controls-group--install"
        role="group"
        aria-label="Install app"
        data-install-controls
        data-install-state="hidden"
      >
        <button
          type="button"
          class="control-button"
          data-action="install"
          title="Install for offline play"
          disabled
        >
          Install app
        </button>
        <p class="controls-note" data-install-note>Install prompt appears once ready.</p>
      </div>
    </footer>
    <p class="sr-only" data-status>Ready.</p>
  `;

  const liveRegion = createLiveRegion(doc);
  shell.append(liveRegion);
  root.replaceChildren(shell);

  const stage = shell.querySelector<HTMLElement>('[data-stage]');
  const keyboardHost = shell.querySelector<HTMLElement>('[data-keyboard]');
  const currentStreak = shell.querySelector<HTMLElement>('[data-streak]');
  const bestStreak = shell.querySelector<HTMLElement>('[data-best-streak]');
  const difficultySlider = shell.querySelector<HTMLInputElement>('#difficulty');
  const themeSelect = shell.querySelector<HTMLSelectElement>('#theme');
  const tapToggle = shell.querySelector<HTMLInputElement>('#tap-mode');
  const streakBar = shell.querySelector<HTMLElement>('.streak-bar');
  const controlsSection = shell.querySelector<HTMLElement>('.controls');
  const toastHost = shell.querySelector<HTMLElement>('[data-badge-stack]');
  const installControls = shell.querySelector<HTMLElement>('[data-install-controls]');
  const installButton = shell.querySelector<HTMLButtonElement>('[data-action="install"]');
  const installNote = shell.querySelector<HTMLElement>('[data-install-note]');
  const status = shell.querySelector<HTMLElement>('[data-status]');

  if (
    !stage ||
    !keyboardHost ||
    !currentStreak ||
    !bestStreak ||
    !difficultySlider ||
    !themeSelect ||
    !tapToggle ||
    !streakBar ||
    !controlsSection ||
    !toastHost ||
    !installControls ||
    !installButton ||
    !installNote ||
    !status
  ) {
    throw new Error('Letter Rain: missing required DOM nodes.');
  }

  const win = stage.ownerDocument?.defaultView ?? window;

  const stored = loadSettings();

  const announce = (message: string) => {
    status.textContent = message;
  };

  const clampDifficultyIndex = (value: number) => {
    if (Number.isNaN(value)) {
      return 0;
    }
    return Math.max(0, Math.min(DIFFICULTY_PRESETS.length - 1, value));
  };

  const applyDifficultyMeta = (index: number) => {
    const preset = DIFFICULTY_PRESETS[index];
    if (preset) {
      difficultySlider.setAttribute('aria-valuetext', preset.label);
    } else {
      difficultySlider.removeAttribute('aria-valuetext');
    }
    return preset;
  };

  const themeValues = new Set<ThemeKey>(THEME_OPTIONS.map((option) => option.value));

  let currentTheme: ThemeKey = 'midnight';
  if (stored?.theme && themeValues.has(stored.theme as ThemeKey)) {
    currentTheme = stored.theme as ThemeKey;
  }
  themeSelect.value = currentTheme;
  applyTheme(shell, currentTheme);

  let tapMode = stored?.tapMode ?? true;
  tapToggle.checked = tapMode;

  const storedDifficulty = normalizeDifficultyKey(stored?.difficulty);
  const initialDifficultyKey = storedDifficulty ?? selectInitialDifficulty();
  const initialIndex = clampDifficultyIndex(
    DIFFICULTY_INDEX_BY_KEY.get(initialDifficultyKey) ?? 0
  );
  difficultySlider.max = String(DIFFICULTY_PRESETS.length - 1);
  difficultySlider.value = String(initialIndex);
  const initialPreset = applyDifficultyMeta(initialIndex);
  const initialDifficulty = initialPreset?.key ?? initialDifficultyKey;

  let sessionSeed = normalizeSeed(options.seed);

  const persistSettings = (partial: Partial<StoredSettings>) => {
    const filtered: Partial<StoredSettings> = {};
    for (const [key, value] of Object.entries(partial)) {
      if (value !== undefined) {
        (filtered as Record<string, unknown>)[key] = value;
      }
    }
    if (Object.keys(filtered).length > 0) {
      saveSettings(filtered);
    }
  };

  const pwaState = {
    serviceWorker: 'unsupported' as ServiceWorkerState,
    installState: 'hidden' as InstallState,
    installOutcome: null as InstallOutcome | null,
    deferredPrompt: null as BeforeInstallPromptEvent | null,
  };

  const updateInstallUi = () => {
    installControls.dataset.installState = pwaState.installState;
    installControls.setAttribute('aria-hidden', pwaState.installState === 'hidden' ? 'true' : 'false');
    shell.dataset.swState = pwaState.serviceWorker;

    let noteMessage = 'Install prompt appears once ready.';

    switch (pwaState.installState) {
      case 'installed':
        installButton.disabled = true;
        installButton.textContent = 'Installed';
        noteMessage = 'Ready to launch from your device.';
        break;
      case 'ready':
        installButton.disabled = false;
        installButton.textContent = 'Install app';
        noteMessage = 'Install for offline play.';
        break;
      case 'unsupported':
        installButton.disabled = true;
        installButton.textContent = 'Install unavailable';
        noteMessage = 'This browser does not support installing Letter Rain.';
        break;
      default:
        installButton.disabled = true;
        installButton.textContent = 'Install app';
        if (pwaState.serviceWorker === 'blocked') {
          noteMessage = 'Service worker blocked; offline install unavailable in this context.';
        } else if (pwaState.serviceWorker === 'unsupported') {
          noteMessage = 'Offline support unavailable in this browser.';
        } else if (pwaState.serviceWorker === 'error') {
          noteMessage = 'Offline install pending; refresh once served securely.';
        }
        break;
    }

    if (pwaState.installOutcome === 'dismissed' && pwaState.installState !== 'installed') {
      noteMessage = 'Install dismissed; reload to try again.';
    } else if (pwaState.installOutcome === 'errored' && pwaState.installState !== 'installed') {
      noteMessage = 'Install failed; check browser settings and try again.';
    }

    installNote.textContent = noteMessage;
  };

  const setInstallState = (state: InstallState) => {
    pwaState.installState = state;
    updateInstallUi();
  };

  const setServiceWorkerStateInternal = (state: ServiceWorkerState) => {
    pwaState.serviceWorker = state;
    if (state === 'unsupported') {
      pwaState.installOutcome = null;
      pwaState.installState = 'unsupported';
    } else if (pwaState.installState === 'hidden' && pwaState.deferredPrompt) {
      pwaState.installState = 'ready';
    }
    updateInstallUi();
  };

  installButton.addEventListener('click', async () => {
    if (!pwaState.deferredPrompt) {
      pwaState.installOutcome = 'errored';
      updateInstallUi();
      return;
    }

    const promptEvent = pwaState.deferredPrompt;
    pwaState.deferredPrompt = null;

    try {
      await promptEvent.prompt();
      const result = await promptEvent.userChoice;
      if (result.outcome === 'accepted') {
        pwaState.installOutcome = 'accepted';
        setInstallState('installed');
      } else {
        pwaState.installOutcome = 'dismissed';
        setInstallState(pwaState.serviceWorker === 'unsupported' ? 'unsupported' : 'hidden');
      }
    } catch (error) {
      console.warn('Letter Rain: install prompt failed', error);
      pwaState.installOutcome = 'errored';
      setInstallState(pwaState.serviceWorker === 'unsupported' ? 'unsupported' : 'hidden');
    }
  });

  const pwaBridge = {
    setInstallPrompt: (event: BeforeInstallPromptEvent | null) => {
      pwaState.deferredPrompt = event;
      if (event) {
        pwaState.installOutcome = null;
        setInstallState('ready');
      } else if (pwaState.installState !== 'installed') {
        setInstallState(pwaState.serviceWorker === 'unsupported' ? 'unsupported' : 'hidden');
      }
    },
    markInstalled: () => {
      pwaState.installOutcome = 'accepted';
      setInstallState('installed');
    },
    reportInstallOutcome: (outcome: InstallOutcome) => {
      pwaState.installOutcome = outcome;
      if (outcome === 'accepted') {
        setInstallState('installed');
      } else if (outcome === 'dismissed') {
        setInstallState('hidden');
      } else {
        setInstallState(pwaState.serviceWorker === 'unsupported' ? 'unsupported' : 'hidden');
      }
    },
    setServiceWorkerState: setServiceWorkerStateInternal,
    getStatus: (): PwaStatusSnapshot => ({
      serviceWorker: pwaState.serviceWorker,
      install: pwaState.installState,
      outcome: pwaState.installOutcome,
    }),
  } satisfies LetterRainAppControls['pwa'];

  updateInstallUi();

  let snapshotInitialized = false;
  const handleSnapshot = (snapshot: GameSnapshot) => {
    if (!snapshotInitialized) {
      snapshotInitialized = true;
      persistSettings({ difficulty: snapshot.difficulty });
    }
    persistSettings({
      difficulty: snapshot.difficulty,
      bestStreak: snapshot.bestStreak,
      achievementsUnlocked: snapshot.achievements,
      lastAchievementAt: snapshot.lastAchievementAt ?? undefined,
    });
  };

  const controller = new GameController({
    stage,
    keyboardHost,
    toastHost,
    scoreboard: { streak: currentStreak, best: bestStreak },
    liveRegion,
    announce,
    difficulty: initialDifficulty,
    seed: sessionSeed,
    onSnapshot: handleSnapshot,
    initialAchievements: stored?.achievementsUnlocked ?? [],
    frameBudgetMs: 16.7,
  });

  controller.start({ silent: true });

  const handleStageTap = (event: PointerEvent) => {
    if (!tapMode) {
      return;
    }
    if (event.pointerType === 'mouse' && event.button !== 0) {
      return;
    }
    const path = event.composedPath();
    const letterNode = path.find(
      (node) =>
        node instanceof HTMLElement &&
        node.classList.contains('rain-letter') &&
        node.dataset.letterId
    ) as HTMLElement | undefined;
    if (!letterNode) {
      return;
    }
    const char = letterNode.textContent?.trim().toLowerCase();
    if (!char) {
      return;
    }
    controller.submitLetter(char, letterNode.dataset.letterId);
  };

  stage.addEventListener('pointerup', handleStageTap, { passive: true });

  if (!stored) {
    persistSettings({
      difficulty: initialDifficulty,
      theme: currentTheme,
      tapMode,
      achievementsUnlocked: [],
    });
  }

  const onDifficultyInput = (event: Event) => {
    const input = event.target as HTMLInputElement;
    const index = clampDifficultyIndex(Number.parseInt(input.value, 10));
    const preset = applyDifficultyMeta(index);
    if (preset) {
      sessionSeed = normalizeSeed(generateSeed());
      controller.setDifficulty(preset.key, { seed: sessionSeed, instant: true });
      persistSettings({ difficulty: preset.key });
      announce(`Difficulty set to ${preset.label}.`);
    } else {
      controller.reset(sessionSeed, { silent: true });
      announce(`Difficulty set to level ${input.value}.`);
    }
    scheduleLayout();
  };

  difficultySlider.addEventListener('input', onDifficultyInput);

  themeSelect.addEventListener('change', (event) => {
    const select = event.target as HTMLSelectElement | null;
    if (!select) {
      return;
    }
    const value = select.value as ThemeKey;
    if (!themeValues.has(value)) {
      return;
    }
    currentTheme = value;
    applyTheme(shell, currentTheme);
    const label = select.options[select.selectedIndex]?.text ?? value;
    announce(`Theme set to ${label}.`);
    persistSettings({ theme: currentTheme });
    scheduleLayout();
  });

  const applyTapMode = (enabled: boolean, { silent = false }: { silent?: boolean } = {}) => {
    tapMode = enabled;
    shell.dataset.tap = enabled ? 'on' : 'off';
    if (tapToggle.checked !== enabled) {
      tapToggle.checked = enabled;
    }
    persistSettings({ tapMode: enabled });
    if (!silent) {
      announce(`Tap targets ${enabled ? 'enabled' : 'disabled'}.`);
    }
  };

  let pendingLayout: number | null = null;

  const recomputeStageMetrics = () => {
    if (!win) {
      return;
    }
    const viewportHeight = win.visualViewport?.height ?? win.innerHeight;
    const shellStyles = win.getComputedStyle(shell);
    const paddingTop = Number.parseFloat(shellStyles.paddingTop) || 0;
    const paddingBottom = Number.parseFloat(shellStyles.paddingBottom) || 0;
    const headerHeight = streakBar?.getBoundingClientRect().height ?? 0;
    const keyboardHeightMeasured = keyboardHost?.getBoundingClientRect().height ?? 0;
    const controlsHeight = controlsSection?.getBoundingClientRect().height ?? 0;
    const available =
      viewportHeight - paddingTop - paddingBottom - headerHeight - keyboardHeightMeasured - controlsHeight - 32;
    const stageHeight = Math.max(420, Math.min(Number.isFinite(available) ? available : 0, 820));
    const keyboardHeight = Math.max(140, keyboardHeightMeasured || stageHeight * 0.25);
    shell.style.setProperty('--stage-height', `${stageHeight}px`);
    shell.style.setProperty('--keyboard-height', `${keyboardHeight}px`);
  };

  const scheduleLayout = () => {
    if (!win) {
      return;
    }
    if (pendingLayout !== null) {
      win.cancelAnimationFrame(pendingLayout);
    }
    pendingLayout = win.requestAnimationFrame(() => {
      pendingLayout = null;
      recomputeStageMetrics();
    });
  };

  tapToggle.addEventListener('change', (event) => {
    const checkbox = event.target as HTMLInputElement | null;
    if (!checkbox) {
      return;
    }
    applyTapMode(checkbox.checked);
    scheduleLayout();
  });

  applyTapMode(tapMode, { silent: true });
  scheduleLayout();

  win.addEventListener('resize', scheduleLayout, { passive: true });
  win.visualViewport?.addEventListener('resize', scheduleLayout);

  if (win.matchMedia?.('(display-mode: standalone)').matches || (navigator as Navigator & { standalone?: boolean }).standalone) {
    pwaBridge.markInstalled();
    pwaBridge.setInstallPrompt(null);
  }

  if (!('serviceWorker' in navigator)) {
    pwaBridge.setServiceWorkerState('unsupported');
  } else if (navigator.serviceWorker.controller) {
    pwaBridge.setServiceWorkerState('active');
  } else {
    pwaBridge.setServiceWorkerState('pending');
  }

  const registerServiceWorker = async () => {
    if (!('serviceWorker' in navigator)) {
      return;
    }
    try {
      const registration = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
      if (!registration) {
        pwaBridge.setServiceWorkerState('blocked');
        return;
      }
      if (registration.active) {
        pwaBridge.setServiceWorkerState('active');
      } else if (registration.installing || registration.waiting) {
        const attach = (worker: ServiceWorker | null) => {
          worker?.addEventListener('statechange', () => {
            if (registration.active) {
              pwaBridge.setServiceWorkerState('active');
            }
          });
        };
        attach(registration.installing ?? null);
        attach(registration.waiting ?? null);
      }
      navigator.serviceWorker.ready
        .then(() => pwaBridge.setServiceWorkerState('active'))
        .catch((error) => {
          console.warn('Letter Rain: service worker ready blocked', error);
          pwaBridge.setServiceWorkerState('blocked');
        });
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      console.warn('Letter Rain: service worker registration failed', error);
      if (error instanceof DOMException && error.name === 'SecurityError') {
        pwaBridge.setServiceWorkerState('blocked');
      } else if (message.toLowerCase().includes('blocked')) {
        pwaBridge.setServiceWorkerState('blocked');
      } else {
        pwaBridge.setServiceWorkerState('error');
      }
    }
  };

  if ('serviceWorker' in navigator) {
    if (document.readyState === 'complete') {
      registerServiceWorker().catch(() => undefined);
    } else {
      window.addEventListener('load', () => registerServiceWorker().catch(() => undefined), {
        once: true,
      });
    }
  }

  const beforeInstallHandler = (event: Event) => {
    event.preventDefault();
    pwaBridge.setInstallPrompt(event as BeforeInstallPromptEvent);
  };

  const onAppInstalled = () => {
    pwaBridge.markInstalled();
    pwaBridge.setInstallPrompt(null);
  };

  window.addEventListener('beforeinstallprompt', beforeInstallHandler);
  window.addEventListener('appinstalled', onAppInstalled);

  return {
    destroy: () => {
      controller.destroy();
      difficultySlider.replaceWith(difficultySlider.cloneNode(true));
      themeSelect.replaceWith(themeSelect.cloneNode(true));
      tapToggle.replaceWith(tapToggle.cloneNode(true));
      installButton.replaceWith(installButton.cloneNode(true));
      installControls.dataset.installState = 'hidden';
      installControls.setAttribute('aria-hidden', 'true');
      delete shell.dataset.swState;
      stage.removeEventListener('pointerup', handleStageTap);
      window.removeEventListener('beforeinstallprompt', beforeInstallHandler);
      window.removeEventListener('appinstalled', onAppInstalled);
      if (pendingLayout !== null) {
        win.cancelAnimationFrame(pendingLayout);
      }
      win.removeEventListener('resize', scheduleLayout);
      win.visualViewport?.removeEventListener('resize', scheduleLayout);
      root.replaceChildren();
    },
    updateStatus: announce,
    debug: {
      step: (milliseconds: number, debugOptions?: { force?: boolean }) => {
        controller.debugStep(milliseconds, debugOptions ?? {});
      },
      isRunning: () => controller.isRunning(),
      letters: () => controller.debugLetters(),
    },
    pwa: pwaBridge,
  };
}

function selectInitialDifficulty(): DifficultyKey {
  const coarsePointer = typeof window !== 'undefined' && window.matchMedia?.('(pointer: coarse)').matches;
  const touchPoints = typeof navigator !== 'undefined' ? navigator.maxTouchPoints : 0;
  if (coarsePointer || touchPoints > 0) {
    return 'level2';
  }
  return 'level3';
}
